import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { AskUserQuestionTool } from 'js/src/agent/tools/AskUserQuestionTool';
import { SketchUrlFetchTool } from 'js/src/agent/tools/sketch/SketchUrlFetchTool';

export const thirdPartyVideoLinkSkill: Skill = {
    name: SKILL_NAMES.thirdPartyVideoLink,
    description:
        'The user pasted a link to a video hosted elsewhere (YouTube, TikTok, Vimeo, Drive, etc.). Load BEFORE responding or fetching it.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Third-Party Video Links

The user pasted a link to a video hosted on someone else's platform — YouTube,
YouTube Shorts, TikTok, Vimeo, Instagram, Facebook, Loom, Google Drive, or a
direct .mp4/.mov.

## We cannot import that video. Say so first.
We can't download, watch, transcribe, clip, trim, reformat, or re-upload a video
from someone else's platform. Lumen5 has no access to the file.

**Do not call \`${SketchUrlFetchTool.toolName}\` on a video link.**

**Never silently substitute.** Building a stock-footage video "inspired by" the link
without saying we couldn't use it won't work well.

## Then find out what they actually wanted
**Ask by calling \`${AskUserQuestionTool.toolName}\` — always, in the same turn.** Writing
the options out as a prose list and stopping leaves the user nothing to click, and the
turn ends without an answer. The question goes through the tool.

Depending on context and what the user already specified, they might be looking for one of these:
- **Use it as a style reference** — they want a new video with a similar look, pacing,
  or format. We can't view it, so ask them to describe the style or upload the video.
- **Make a video on the same topic** — we build a fresh one from a script, a document,
  an article URL, or a topic they describe. Ask them to give the topic/source or upload the video.
- **Repurpose footage they own** — if it is *their* video, they can upload the file
  directly. Uploading is the only way footage gets into Lumen5.`;
    },
};
